import type { PermissionCode } from '../types';

/**
 * Papéis do DataCrazy como aparecem em "Convidar membro" / "Editar papéis"
 * (doc 01, seção 3). Sem Impulsos e sem Agentes de IA, por decisão.
 */
export const PERMISSION_GROUPS: Array<{
  group: string;
  /** Vale também para administradores (o resto, o administrador já tem). */
  alsoForAdmins?: boolean;
  items: Array<{ code: PermissionCode; label: string; description: string }>;
}> = [
  {
    group: 'Pipelines',
    items: [
      {
        code: 'pipeline_admin',
        label: 'Administrador de Pipelines',
        description: 'Criar, modificar, duplicar e configurar pipelines',
      },
      {
        code: 'pipeline_member',
        label: 'Membro de Pipelines',
        description: 'Manter negócios na pipeline',
      },
    ],
  },
  {
    group: 'Automações',
    items: [
      {
        code: 'flow_admin',
        label: 'Administrador das Automações',
        description: 'Ver automações e todas as ações sobre elas',
      },
      { code: 'flow_member', label: 'Membro das Automações', description: 'Ver automações' },
    ],
  },
  {
    group: 'Cadastros auxiliares',
    items: [
      {
        code: 'aux_admin',
        label: 'Administrador de Cadastros Auxiliares',
        description: 'Criar, editar e excluir produtos, tags, listas, motivos, tipos e campos',
      },
      {
        code: 'aux_member',
        label: 'Membro de Cadastros Auxiliares',
        description: 'Ver a listagem dos cadastros auxiliares',
      },
    ],
  },
  {
    group: 'Leads',
    items: [
      {
        code: 'leads_admin',
        label: 'Administrador de Leads',
        description: 'Listagem de leads e todas as ações sobre eles',
      },
      {
        code: 'leads_member_operator',
        label: 'Operador de Leads',
        description: 'Criar e alterar leads (usar junto com "Membro de Leads (restrito)")',
      },
      {
        code: 'leads_member',
        label: 'Membro de Leads',
        description: 'Ver a listagem de leads',
      },
      {
        code: 'leads_member_restrict',
        label: 'Membro de Leads (restrito)',
        description: 'Só os leads de que é responsável e os negócios atribuídos a ele',
      },
      {
        code: 'allow-lead-export',
        label: 'Permitir exportação de leads',
        description: 'Ação sensível — só para administradores de leads',
      },
    ],
  },
  {
    group: 'Multiatendimento',
    items: [
      {
        code: 'multiservice_supervisor',
        label: 'Administrador de multiatendimento',
        description: 'Multiatendimento, dashboard e configurações completos',
      },
      {
        code: 'multiservice_attendant_supervisor',
        label: 'Supervisor de multiatendimento',
        description: 'Multiatendimento e dashboard, respeitando as permissões',
      },
      {
        code: 'multiservice_attendant',
        label: 'Atendente de multiatendimento',
        description: 'Multiatendimento limitado pelas permissões',
      },
    ],
  },
  {
    group: 'Atividades',
    items: [
      {
        code: 'activities_admin',
        label: 'Administrador de Atividades',
        description: 'Calendário de atividades de todos os atendentes',
      },
    ],
  },
  {
    // No módulo Vendas, ser administrador do CRM não basta para ver as propostas de
    // todos: cada proposta é do vendedor dela (Q13). Por isso vale também para admins.
    group: 'Vendas',
    alsoForAdmins: true,
    items: [
      {
        code: 'sales_admin',
        label: 'Gestor de vendas',
        description:
          'Vê e edita as propostas de todos os vendedores, a comissão de cada um e as faixas',
      },
      {
        code: 'sales_member',
        label: 'Vendedor',
        description:
          'Cadastra e acompanha só as próprias propostas; ninguém além do gestor as vê',
      },
    ],
  },
];

/** O que um administrador guarda de papéis: só os que valem também para ele. */
export const adminPermissions = (codes: PermissionCode[]): PermissionCode[] =>
  codes.filter((code) =>
    PERMISSION_GROUPS.some(
      (group) => group.alsoForAdmins && group.items.some((item) => item.code === code),
    ),
  );

const LABELS = new Map(
  PERMISSION_GROUPS.flatMap((group) =>
    group.items.map((item) => [item.code, item.label] as const),
  ),
);

export const permissionLabel = (code: PermissionCode): string => LABELS.get(code) ?? code;
